

import React, { useState, useEffect } from "react";
import { useAuth } from "auth/Auth";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {
  Card,
  Grid,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  FormHelperText
} from "@mui/material";


function SupportTicket() {
  const { user, license } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    description: '',
    type: '',
    company: '',
    priority: 'Medium',
    tenantName: '',
    billingType: '',
    issueWith: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Auto-populate form from auth context
  useEffect(() => {
    if (!user) {
      return;
    }

    let userInfo = { ...user };

    if (user.username) {
      const [nameSection] = user.username.split('@');
      const [firstName, lastName] = nameSection.split('.');
      const fullName = `${firstName.charAt(0).toUpperCase()}${firstName.slice(1)} ${lastName ? lastName.charAt(0).toUpperCase() + lastName.slice(1) : ''}`;
      const companySection = user.username.split('@')[1];
      const companyName = companySection ? companySection.charAt(0).toUpperCase() + companySection.slice(1) : '';
      const tenantName = companySection ? `tenant_${companySection.toLowerCase()}` : '';

      userInfo = {
        ...userInfo,
        parsedName: fullName.trim(),
        parsedCompany: companyName,
        parsedTenant: tenantName,
      };
    }

    setFormData((prev) => ({
      ...prev,
      name: userInfo.parsedName || userInfo.fullName || userInfo.name || '',
      email: userInfo.email || '',
      company: userInfo.parsedCompany || userInfo.company || userInfo.businessName || '',
      tenantName: userInfo.parsedTenant || userInfo.tenantName || '',
      // license_type is the plan's name (plans.name), so comparing it to
      // 'Trial' was only ever true for a plan literally called that.
      // Trial-ness is its own flag.
      billingType: license?.is_trial ? 'Trial User' : 'Paid User',
      issueWith: '',
    }));
  }, [user, license]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log('Handling change:', { name, value }); // Debug log
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      console.log('Updated form data:', newData); // Debug log
      return newData;
    });
  };

  const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  const validateForm = () => {
    const requiredFields = ['type', 'priority', 'issueWith', 'subject', 'description'];
    const emptyFields = requiredFields.filter(field => !formData[field]);
    
    if (emptyFields.length > 0) {
      setSubmitStatus({
        type: 'error',
        message: 'Please fill in all required fields'
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    // Prepare data for webhook
    const webhookData = {
      contactName: formData.name,
      email: formData.email,
      phone: formData.phone || 'Not provided',
      company: formData.company,
      tenantName: formData.tenantName,
      billingType: formData.billingType,
      ticketType: formData.type,
      priority: formData.priority,
      issueWith: formData.issueWith,
      subject: formData.subject,
      description: formData.description,
      submittedAt: new Date().toISOString()
    };

    if (isDevelopment) {
      console.log('Development mode - webhook data:', webhookData);
      setTimeout(() => {
        setSubmitStatus({ 
          type: 'success',
          message: 'Your support ticket has been submitted successfully, case details would be emailed to you shortly. Our team will get back to you within 24-48 hours.'
        });
        // Reset form fields except the auto-populated ones
        setFormData(prev => ({
          ...prev,
          phone: '',
          subject: '',
          description: '',
          type: '',
          priority: 'Medium',
          issueWith: ''
        }));
        setLoading(false);
      }, 1000);
      return;
    }

    try {
      const response = await fetch('https://hook.eu2.make.com/j5wgs0oixtzm6t12h4templbp0yecl14', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSubmitStatus({
        type: 'success',
        message: 'Your support ticket has been submitted successfully, case details would be emailed to you shortly. Our team will get back to you within 24-48 hours.'
      });

      // Reset form fields except the auto-populated ones
      setFormData(prev => ({
        ...prev,
        phone: '',
        subject: '',
        description: '',
        type: '',
        priority: 'Medium',
        issueWith: ''
      }));
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        type: 'error',
        message: 'There was an error submitting your support ticket. Please try again or contact support directly.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6} justifyContent="center">
          <Grid item xs={12} md={8}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Create Support Ticket
                </MDTypography>
              </MDBox>
              
              <MDBox p={3}>
                {submitStatus && (
                  <Alert 
                    severity={submitStatus.type} 
                    sx={{ 
                      mb: 3,
                      '& .MuiAlert-message': {
                        width: '100%'
                      }
                    }}
                  >
                    {submitStatus.message}
                  </Alert>
                )}
                
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    {/* First Row: Contact Name and Email */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Contact Name"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        disabled
                        required
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        disabled
                        required
                      />
                    </Grid>

                    {/* Second Row: Company and Tenant Name */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Company"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        disabled
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Tenant Name"
                        id="tenantName"
                        name="tenantName"
                        value={formData.tenantName}
                        onChange={handleChange}
                        disabled
                      />
                    </Grid>

                    {/* Third Row: Phone and Billing Type */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Phone"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Billing Type"
                        id="billingType"
                        name="billingType"
                        value={formData.billingType}
                        onChange={handleChange}
                        disabled
                      />
                    </Grid>

                    {/* Fourth Row: Type and Priority */}
                    <Grid item xs={12} md={6}>
                      <FormControl 
                        fullWidth 
                        required
                        sx={{ 
                          '& .MuiInputBase-root': {
                            height: '56px'
                          }
                        }}
                      >
                        <InputLabel id="type-label">Type</InputLabel>
                        <Select
                          labelId="type-label"
                          id="type"
                          name="type"
                          value={formData.type}
                          label="Type"
                          onChange={handleChange}
                        >
                          <MenuItem value="">--None--</MenuItem>
                          <MenuItem value="Problem">Problem</MenuItem>
                          <MenuItem value="Feature Request">Feature Request</MenuItem>
                          <MenuItem value="Question">Question</MenuItem>
                          <MenuItem value="Login">Login</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <FormControl 
                        fullWidth 
                        required
                        sx={{ 
                          '& .MuiInputBase-root': {
                            height: '56px'
                          }
                        }}
                      >
                        <InputLabel id="priority-label">Priority</InputLabel>
                        <Select
                          labelId="priority-label"
                          id="priority"
                          name="priority"
                          value={formData.priority}
                          label="Priority"
                          onChange={handleChange}
                        >
                          <MenuItem value="High">High</MenuItem>
                          <MenuItem value="Medium">Medium</MenuItem>
                          <MenuItem value="Low">Low</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Fifth Row: Issue With */}
                    <Grid item xs={12}>
                      <FormControl 
                        fullWidth 
                        required
                        sx={{ 
                          '& .MuiInputBase-root': {
                            height: '56px'
                          }
                        }}
                      >
                        <InputLabel id="issue-with-label">Issue With</InputLabel>
                        <Select
                          labelId="issue-with-label"
                          id="issueWith"
                          name="issueWith"
                          value={formData.issueWith}
                          label="Issue With"
                          onChange={handleChange}
                        >
                          <MenuItem value="">--None--</MenuItem>
                          <MenuItem value="Leads">Leads</MenuItem>
                          <MenuItem value="Deals">Deals</MenuItem>
                          <MenuItem value="Accounts">Accounts</MenuItem>
                          <MenuItem value="Contacts">Contacts</MenuItem>
                          <MenuItem value="Products">Products</MenuItem>
                          <MenuItem value="Quotes">Quotes</MenuItem>
                          <MenuItem value="Orders">Orders</MenuItem>
                          <MenuItem value="Invoices">Invoices</MenuItem>
                          <MenuItem value="Email">Email</MenuItem>
                          <MenuItem value="Tasks">Tasks</MenuItem>
                          <MenuItem value="Reports">Reports</MenuItem>
                          <MenuItem value="Login">Login</MenuItem>
                          <MenuItem value="WebForms">WebForms</MenuItem>
                          <MenuItem value="AI Features">AI Features</MenuItem>
                          <MenuItem value="Notifications">Notifications</MenuItem>
                          <MenuItem value="Mobile App">Mobile App</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Sixth Row: Subject */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Subject"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    
                    {/* Seventh Row: Description */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        multiline
                        rows={4}
                        required
                      />
                    </Grid>

                    {/* Submit Button */}
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={loading}
                        sx={{ 
                          mt: 3,
                          color: '#ffffff',
                          '&:hover': {
                            color: '#ffffff'
                          },
                          fontWeight: 'medium',
                          textTransform: 'none'
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          "Submit Support Ticket"
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      
    </DashboardLayout>
  );
}

export default SupportTicket; 